import { useSeo } from "@/lib/use-seo";
import { breadcrumbJsonLd } from "@/lib/structured-data";

const PAGE_TITLE =
  "About Accent Productions | Wellington Sound Hire Since 2005";

const PAGE_DESCRIPTION =
  "Wellington's most experienced sound hire team. Since 2005, over 2000 events including 20+ years at Newtown Festival and Cuba Dupa. Professional event audio across New Zealand.";

/**
 * Ported from web/src/app/about/page.tsx (Next.js App Router).
 *
 * - next/metadata export -> useSeo hook (#147): title, description,
 *   canonical, and the BreadcrumbJsonLd from the old about/layout.tsx.
 * - next/image -> plain <img> with absolute-fill classes.
 * - next/link import was unused in the original and is dropped.
 */
export default function AboutPage() {
  useSeo({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    canonical: "/about",
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "About", path: "/about" },
      ]),
    ],
  });

  return (
    <main className="min-h-screen">
      {/* Header — oversized page title */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-28 pb-10 md:pb-20">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95] text-gray-900 mb-5 md:mb-6">
            Twenty years of shows<span className="text-orange-600">.</span>
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-gray-600 max-w-2xl font-medium">
            Wellington&apos;s trusted sound hire team since 2005. Audio for festivals, public events, corporate functions, and private parties across New Zealand.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="pb-16 md:pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="max-w-prose">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-gray-900 mb-8">
                Our Story
              </h2>
              <div className="space-y-6 text-gray-700 text-lg leading-relaxed">
                <p>
                  Accent Productions has been providing sound for events around Wellington since 2005. We&apos;ve done over 2,000 gigs in that time, everything from weddings and corporate functions to multi-stage street festivals.
                </p>
                <p>
                  We bring the gear, set it up, run it on the day, and pack it all out afterwards. If something goes wrong, we sort it. That&apos;s been the deal for 20 years.
                </p>
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-3">Long-term clients</p>
                  <div className="border-t border-gray-200">
                    <p className="py-3 border-b border-gray-200 font-medium text-gray-900">Newtown Festival, 2005 to present</p>
                    <p className="py-3 border-b border-gray-200 font-medium text-gray-900">Cuba Dupa, 2022 to present</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="aspect-[4/3] relative rounded-md overflow-hidden">
              <img
                src="/images/image1-festival-cuba-st.webp"
                alt="Accent Productions sound hire setup at Cuba Dupa festival Wellington since 2005"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <p className="absolute bottom-3 right-3 text-white text-xs font-medium [text-shadow:0_1px_3px_rgba(0,0,0,0.85)]">Wellington CBD, New Zealand</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
